import {ReactNode, useMemo} from 'react';

import {Icon} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {RolesAccount, RoleType} from '@/app/auth/constants/AuthConstants';
import {RaterComponent} from '@/app/shared-components';
import {IMAGES} from '@/constants/images';
import {useAppRates} from '@/dataSource/src/api/firebase/useAppRates';
import useWindowSize from '@/hooks/WindowSize/WindowSize';
import {COLORS} from '@/styles/colors';
import './styles';

const APP_RATE_DOCUMENT_IDS = ['appStore', 'trustpilot'];

export function AuthenticationWrapper({
  title,
  children,
  subtitle,
  phone,
  onBack,
  isVerify = false,
  role = RolesAccount.PARENT,
  showRightPanel = true,
  showBrands = true,
  showFooter = true,
  showFdic = true,
}: {
  children: ReactNode;
  title?: string | ReactNode;
  subtitle?: string;
  onBack?: () => void;
  isVerify?: boolean;
  phone?: string;
  role?: RoleType;
  showRightPanel?: boolean;
  showBrands?: boolean;
  showFooter?: boolean;
  showFdic?: boolean;
}) {
  const {t} = useTranslation();
  const {isMobile} = useWindowSize();
  const {isLoading: isLoadingAppRates, getRateByDevice} = useAppRates({
    documentIds: APP_RATE_DOCUMENT_IDS,
  });

  const isTeen = role === RolesAccount.TEEN;

  const imageClassName = useMemo(
    () => (isMobile ? undefined : `image ${isTeen ? 'teen' : ''}`),
    [isMobile, isTeen],
  );

  const reviewText = useMemo(
    () =>
      role === RolesAccount.PARENT
        ? t('authentication.review.text')
        : t('authentication.review_teen.text'),
    [role, t],
  );

  const reviewUser = useMemo(
    () =>
      role === RolesAccount.PARENT
        ? t('authentication.review.user')
        : t('authentication.review_teen.user'),
    [role, t],
  );

  const titleStyle = useMemo(
    () => ({marginTop: isMobile ? 16 : 0, paddingInline: 16}),
    [isMobile],
  );

  const footer = useMemo(() => {
    return (
      <section className="footer-container">
        {showFdic && (
          <p className="disclaimer">{t('authentication.footer.fdic')}</p>
        )}
        <p className="disclaimer">{t('authentication.footer.based_on')}</p>
      </section>
    );
  }, [showFdic, t]);

  const brands = useMemo(() => {
    const selectedDevice = isMobile ? 'mobile' : 'desktop';
    const appStoreRateData = getRateByDevice('appStore', selectedDevice);
    const trustpilotRateData = getRateByDevice('trustpilot', selectedDevice);

    return (
      <section className="brands-container">
        <RaterComponent
          text={appStoreRateData?.label ?? ''}
          rating={appStoreRateData?.rate ?? 0}
        />
        <RaterComponent
          text={trustpilotRateData?.label ?? ''}
          rating={trustpilotRateData?.rate ?? 0}
        />
      </section>
    );
  }, [getRateByDevice, isMobile]);

  return (
    <div className="body-container">
      <section className="w-layout-block-container home-hero-container">
        <div className="left">
          <div className="modak-logo-container">
            <img
              src={IMAGES.LOGO_TEXT_BLACK.src}
              height="auto"
              className="modak-logo"
            />
          </div>
          <section
            style={{
              maxWidth: 496,
              minWidth: 300,
              alignSelf: 'left',
            }}>
            {onBack && (
              <>
                <div
                  className="back"
                  onClick={onBack}>
                  <Icon
                    style={{
                      fontSize: 20,
                      color: COLORS.CONTENT_LIGHT_TERTIARY,
                    }}>
                    arrow_back
                  </Icon>
                </div>
                <div style={{height: '24px'}} />
              </>
            )}
            {title &&
              (typeof title === 'string' ? (
                <h4
                  className="title-page"
                  style={titleStyle}>
                  {title}
                  {phone && <span className="phone-number-text">{phone}</span>}
                </h4>
              ) : (
                <div
                  className="title-page"
                  style={titleStyle}>
                  {title}
                </div>
              ))}

            {subtitle && (
              <h4
                className={isVerify ? '' : 'subtitle-page'}
                style={{paddingInline: 16}}>
                {subtitle}
              </h4>
            )}
            <div className="children-content">
              {children}
              {showBrands && isMobile && !isLoadingAppRates && brands}
            </div>
          </section>
          {!isMobile && showFooter && footer}
        </div>
        {showRightPanel && (
          <div className="right">
            {imageClassName && (
              <div className={imageClassName}>
                {showBrands && !isMobile && !isLoadingAppRates && brands}
                <div className="authentication-image-title">{reviewText}</div>
                <div className="author">{reviewUser}</div>
              </div>
            )}

            {isMobile && showFooter && footer}
          </div>
        )}
      </section>
    </div>
  );
}
